import * as THREE from 'three';

// #region TYPES

interface IPickingMaterial extends THREE.RawShaderMaterial {
  set intID(v: number);
  decodePixel(px: Int32Array, itm: PickingItem): PickingResult;
}

export type PickingItem = { o: THREE.Object3D; m: IPickingMaterial };

export type PickingResult = {
  type: string;
  id: number;
  idx?: number;
  hit?: Array<number>;
  obj?: THREE.Object3D;
};

// #endregion

export class ScreenPicker {
  // #region MAIN
  emptyScene = new THREE.Scene(); // Needed to render an empty scene to get renderList
  targetScene: THREE.Scene; // The scene that created the last renderList
  targetRenderer: THREE.WebGLRenderer;
  targetCamera:
    | THREE.Camera
    | THREE.PerspectiveCamera
    | THREE.OrthographicCamera;

  bakClearColor = new THREE.Color(); // Backup Clear color of renderer
  nulClearColor = new THREE.Color().setRGB(-1, -1, -1); // Clear color for picking
  idMapper: Record<number, PickingItem> = {};

  texPoint: THREE.WebGLRenderTarget = genIntRenderTarget(1, 1, 4); // 1x1 Pixel storing RGBA

  materials = {
    meshHit: new PickingHitIDMaterial(), // Render meshes to get ID and Hit World Space Position
    shapeQuad: new PickingShapeQuadsMaterial(),
  };

  constructor(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    camera: THREE.Camera | THREE.PerspectiveCamera | THREE.OrthographicCamera,
  ) {
    this.emptyScene.onAfterRender = this.onAfterRender;
    this.targetRenderer = renderer;
    this.targetScene = scene;
    this.targetCamera = camera;
  }
  // #endregion

  // #region PICKING METHODS
  pickPoint(p: Array<number>) {
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Set the render viewport size, for this mode we
    // only need to render a single pixel worth
    const canvas = this.targetRenderer.domElement;
    const dpr = window.devicePixelRatio;
    if ('setViewOffset' in this.targetCamera) {
      this.targetCamera.setViewOffset(
        canvas.width, // fullWidth
        canvas.height, // fullHeight
        Math.floor(p[0] * dpr), // X
        Math.floor(p[1] * dpr), // Y
        1, // Width
        1, // Height
      );
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Execute
    this.pickingRender(this.texPoint);

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Read Pixel
    const pxBuf = new Int32Array(4);
    this.targetRenderer.readRenderTargetPixels(
      this.texPoint,
      0,
      0,
      1,
      1,
      pxBuf,
    );

    // console.log('PixelData', pxBuf);

    if (pxBuf[0] < 0) return { type: 'noPick', id: -1 };

    const itm = this.idMapper[pxBuf[0]];
    return itm.m.decodePixel(pxBuf, itm);
  }
  // #endregion

  // #region RENDERING
  // Begin render process to compute texture that will hold the pixel ID
  pickingRender(tex: THREE.WebGLRenderTarget) {
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Setup
    this.idMapper = {};
    const cam = this.targetCamera;
    const rend = this.targetRenderer;

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Need to render an empty scene with a post render callback setup
    // to properly have access to internal bits that allows us to
    // render objects manually along with getting a list of objects
    // that was used to render the last frame. This list is how we can
    // use the main scene to render without needing to keep track
    // of a picking specific scene with cloned meshes.
    rend.setRenderTarget(tex); // Set which texture to render to
    rend.getClearColor(this.bakClearColor); // Backup existing clear color
    rend.setClearColor(this.nulClearColor, -1); // Set new clear color used for picking

    rend.render(this.emptyScene, cam);

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Cleanup
    if ('clearViewOffset' in cam) {
      cam.clearViewOffset();
    }
    rend.setRenderTarget(null);
    rend.setClearColor(this.bakClearColor, 0);
  }

  // Event bound to emptyScene, helps trigger rendering to the texture
  // this is needed to keep alive currentRenderState & grab last renderList
  onAfterRender = () => {
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Get the last render list done by the renderer
    const rl = this.targetRenderer.renderLists.get(this.targetScene, 0); // scene, renderCallDepth
    const merge = rl.opaque.concat(rl.transmissive, rl.transparent);

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Manually render each object
    let intID: number;
    let pickMat: IPickingMaterial;

    for (const i of merge) {
      pickMat = this.materials.meshHit;

      // ----------------------------------
      // Filter out objects that can be used by picking plus set the proper material
      // console.log( i.object.name, i.object.type, i.object.id, i.object.uuid );
      switch (i.object.type) {
        case 'Mesh':
          switch (i.object.name) {
            case 'ShapeQuads':
            case 'RingQuads':
              pickMat = this.materials.shapeQuad;
              pickMat.depthTest = i.material.depthTest;
              break;
          }

          // if (i.object.name === 'ShapeQuads') {
          //   pickMat = this.materials.shapeQuad;
          //   pickMat.depthTest = i.material.depthTest;
          // }
          break;

        // case 'Points':break;
        // case 'SkinnedMesh': break;

        // Skip this object
        default:
          continue;
      }

      // ----------------------------------
      intID = i.object.id;

      // Update picking material with object's ID
      pickMat.intID = intID;
      this.idMapper[intID] = { o: i.object, m: pickMat };

      // ----------------------------------
      // NOTE: renderBufferDirect only works on a scene.onAfterRender else an error out
      // because currentRenderState is null. To handle this issue, render an empty scene
      // which will then do nothing but still have access to a set render state while still
      // having access to the previous frame's renderLists
      if (i.geometry) {
        this.targetRenderer.renderBufferDirect(
          this.targetCamera,
          this.emptyScene,
          i.geometry,
          pickMat,
          i.object,
          null,
        );
      }
    }
  };
  // #endregion

  // #region TASKS

  pixelTaskFromEvent(
    e: PointerEvent,
    fn: (r: PickingResult) => void,
    alwaysCall = false,
  ): () => void {
    return () => {
      const rect = this.targetRenderer.domElement.getBoundingClientRect();
      const pos = [e.clientX - rect.left, e.clientY - rect.top];
      const dat = this.pickPoint(pos);
      if (alwaysCall || dat.id !== -1) fn(dat);
    };
  }

  // #endregion
}

// #region HELPERS

function genIntRenderTarget(w = 1, h = 1, size = 4): THREE.WebGLRenderTarget {
  let format: number = -1;
  switch (size) {
    case 1:
      format = THREE.RedIntegerFormat;
      break;
    case 4:
      format = THREE.RGBAIntegerFormat;
      break;
  }

  return new THREE.WebGLRenderTarget(w, h, {
    minFilter: THREE.NearestFilter,
    magFilter: THREE.NearestFilter,
    type: THREE.IntType,
    format,
  });
}

// #endregion

// #region PICKING MATERIALS

class PickingHitIDMaterial
  extends THREE.RawShaderMaterial
  implements IPickingMaterial
{
  constructor() {
    super();

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    this.name = 'PickingHitIDMaterial';
    this.glslVersion = THREE.GLSL3;
    this.depthTest = true;
    this.blending = THREE.NoBlending;
    this.uniforms = { intID: { value: 0 } };

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    this.vertexShader = `
    in vec3 position;

    uniform mat4 modelMatrix;
    uniform mat4 viewMatrix;
    uniform mat4 projectionMatrix;
    uniform mat4 modelViewMatrix;

    out vec3 fragWPos;

    void main(){
        vec4 wPos     = modelMatrix * vec4( position, 1.0 );  // World Space
        vec4 vPos     = viewMatrix * wPos;                    // View Space
        gl_Position   = projectionMatrix * vPos;              // Projection Space
        fragWPos      = wPos.xyz;                             // Worldspace Vertex Position
    }`;

    this.fragmentShader = `
    precision mediump float;

    uniform int  intID;
    in      vec3 fragWPos;
    layout(location=0) out ivec4 outData;
    
    const float toInt = 10000.0; // Scale float values so they can sit inside an int32

    void main(){ outData = ivec4( intID, ivec3( fragWPos * toInt ) ); }`;
  }

  set intID(v: number) {
    this.uniforms.intID.value = v;
    this.uniformsNeedUpdate = true;
  }

  decodePixel(px: Int32Array, itm: PickingItem): PickingResult {
    const floatScale = 10000;
    return {
      type: 'meshHit',
      obj: itm.o,
      id: px[0],
      hit: [px[1] / floatScale, px[2] / floatScale, px[3] / floatScale],
    };
  }
}

class PickingShapeQuadsMaterial
  extends THREE.RawShaderMaterial
  implements IPickingMaterial
{
  constructor() {
    super();

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    this.name = 'PickingShapeQuads';
    this.glslVersion = THREE.GLSL3;
    this.depthTest = true;
    this.side = THREE.DoubleSide;
    this.blending = THREE.NoBlending;
    this.transparent = false;
    this.alphaToCoverage = false;

    this.uniforms = {
      intID: { value: 0 },
      uniScale: { value: 0.03 },
    };

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    this.vertexShader = `
    in vec3 position;
    in vec3 ipos;
    in vec3 icol;
    in vec2 icfg; // [size,shape]

    uniform mat4 projectionMatrix;
    uniform mat4 viewMatrix;
    uniform mat4 modelMatrix;
    
    uniform float uniScale;

    flat out int objIdx;

    void main(){
        // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
        // Get the transposed positions if we treat the viewMatrix as a 3x# matrix
        // This allows us to avoid using invert to get the camera axes
        vec3 xCam = vec3( viewMatrix[0].x, viewMatrix[1].x, viewMatrix[2].x );
        vec3 yCam = vec3( viewMatrix[0].y, viewMatrix[1].y, viewMatrix[2].y );
        vec3 zCam = vec3( viewMatrix[0].z, viewMatrix[1].z, viewMatrix[2].z );

        // Spherical billboarding position
        // Use the model-view matrix to get the correct camera axes after group rotation
        mat4 mvMatrix = viewMatrix * modelMatrix;
        vec3 xCamMV = vec3( mvMatrix[0].x, mvMatrix[1].x, mvMatrix[2].x );
        vec3 yCamMV = vec3( mvMatrix[0].y, mvMatrix[1].y, mvMatrix[2].y );

        vec3 bPos = ipos 
            + ( xCamMV * position.x * uniScale * icfg.x ) 
            + ( yCamMV * position.y * uniScale * icfg.x );

        // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
        vec4 wPos   = modelMatrix * vec4( bPos, 1.0 );
        gl_Position = projectionMatrix * viewMatrix * wPos;

        // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
        objIdx = gl_InstanceID;
    }`;

    this.fragmentShader = `
    precision mediump float;

    uniform int intID;
    flat in int objIdx;

    layout(location=0) out ivec4 outData;

    void main(){ outData = ivec4( intID, objIdx, 0, 0 ); }`;
  }

  set intID(v: number) {
    this.uniforms.intID.value = v;
    this.uniformsNeedUpdate = true;
  }

  decodePixel(px: Int32Array, itm: PickingItem): PickingResult {
    return {
      type: 'shapeQuad',
      obj: itm.o,
      id: px[0],
      idx: px[1],
      hit: (itm.o as any).getPosAt(px[1]),
    };
  }
}

// #endregion

// #region TASK QUEUE

export class Tasker {
  queue: Array<() => void> = [];
  constructor() {}

  push(task: () => void) {
    this.queue.push(task);
    return this;
  }

  run() {
    if (this.queue.length === 0) return;

    // Copy reference and start a new queue.
    // Tasks can potentially create new tasks, so dont want
    // to create the possiblily of an infinite loop where a
    // queue is never empty.
    const q = this.queue;
    this.queue = [];

    for (const fn of q) {
      try {
        fn();
      } catch (err) {
        console.error(`Error executing task:`, err);
      }
    }
  }
}

// #endregion
